import React, { FC, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Modal, ModalBody, ModalFooter, ModalHeader, Table } from 'reactstrap';
import { CashierCheck } from '../../../shared/user/cashier';
import { DateT, HotkeyButton, MaterialIcon } from '../../components';
import { TranslationKey, useTranslation } from '../../translations';
import { useFetcher } from '../../utils';
import { DriversLicenseResult, parseDriversLicenseResult, useScannerResult } from './scanner';

interface IdProps {
  readonly id: number;
}

interface IDValidationProps extends IdProps {
  readonly onOpen: () => void;
  readonly onClose: (barcode?: string) => void;
}

export const IDValidation: FC<IDValidationProps> = ({ id, onOpen, onClose }) => {
  const { ts } = useTranslation();
  const [barcode, setBarcode] = useState<string | null>(null);

  useScannerResult((result) => {
    if ('uuid' in result) {
      // ConCatUUIDResult is not valid for ID validation
      toast.error(ts('cashier_ui.idvalidation.reg_qr_code_not_valid'), { autoClose: 5000 });

      return;
    }

    setBarcode(parseDriversLicenseResult(result as DriversLicenseResult));
  }, []);

  useEffect(() => {
    if (barcode) {
      onOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- adding onOpen causes infinite loop
  }, [barcode]);

  const onInnerClose = useCallback(
    (success?: boolean) => {
      onClose(success && barcode ? barcode : undefined);
      setBarcode(null);
    },
    [barcode, onClose],
  );

  return barcode ? <IDValidationModal barcode={barcode} id={id} onClose={onInnerClose} /> : null;
};

interface ModalProps extends IdProps {
  readonly barcode: string;
  readonly onClose: (success?: boolean) => void;
}

const IDValidationModal: FC<ModalProps> = ({ barcode, id, onClose }) => {
  const { ts } = useTranslation();
  const fetcher = useFetcher(
    async () => await api.getUserCashierProfile(id, barcode),
    [id, barcode],
  );

  const isLoading = fetcher.loading;
  const isExpired = fetcher.data?.licenseWarnings.includes('expired');

  const licenseWarnings = fetcher.data?.licenseWarnings.filter((val) => val !== 'expired') ?? [];
  const hasWarnings = licenseWarnings.length > 0;

  const isValid = !hasWarnings && !isExpired;
  const isOnlyExpired = !hasWarnings && isExpired;

  const closeText: TranslationKey = isLoading ? 'common.cancel' : 'common.close';

  return (
    <Modal isOpen>
      <ModalHeader>{ts('cashier_ui.idvalidation.title')}</ModalHeader>
      <ModalBody>
        {isLoading && <IDValidationPending />}
        {!isLoading && hasWarnings && <IDValidationFailed status={fetcher.data!} />}
        {!isLoading && isOnlyExpired && <IDValidationExpired />}
        {!isLoading && isValid && <IDValidationSuccess />}
        <hr />
        <p className="text-secondary text-center text-wrap">
          <small>{barcode}</small>
        </p>
      </ModalBody>
      <ModalFooter>
        <div className="hstack gap-3 w-100">
          {isOnlyExpired && (
            <HotkeyButton
              actionText="cashier_ui.idvalidation.mark_as_valid"
              block
              bypassDisableHotkeys
              color="danger"
              hotkey="Enter"
              onClick={() => {
                onClose(true);
              }}
              outline
              requiredKeyPressCount={3}
            >
              {ts('cashier_ui.idvalidation.mark_as_valid')}
            </HotkeyButton>
          )}
          <HotkeyButton
            actionText={closeText}
            block
            bypassDisableHotkeys
            color="secondary"
            hotkey="Escape"
            onClick={() => {
              onClose(isValid);
            }}
            outline
          >
            {ts(closeText)}
          </HotkeyButton>
        </div>
      </ModalFooter>
    </Modal>
  );
};

const IDValidationPending: FC = () => {
  const { ts } = useTranslation();

  return (
    <div className="d-flex flex-wrap flex-column align-content-center text-center">
      <div className="p-2 text-center flex-fill text-secondary">
        <MaterialIcon large name="pending" />
      </div>
      <h4 className="p-2 flex-fill">{ts('cashier_ui.idvalidation.validating_id')}</h4>
    </div>
  );
};

const IDValidationSuccess: FC = () => {
  const { ts } = useTranslation();

  return (
    <div className="d-flex flex-wrap flex-column align-content-center text-center">
      <div className="p-2 text-center flex-fill text-success">
        <MaterialIcon large name="check_circle" />
      </div>
      <h4 className="p-2 flex-fill">{ts('cashier_ui.idvalidation.id_validated')}</h4>
      <p className="text-muted">
        <small>{ts('cashier_ui.idvalidation.id_validated.description')}</small>
      </p>
    </div>
  );
};

const IDValidationExpired: FC = () => {
  const { ts } = useTranslation();

  return (
    <div className="d-flex flex-wrap flex-column align-content-center text-center">
      <div className="p-2 text-center flex-fill text-warning">
        <MaterialIcon large name="timelapse" />
      </div>
      <h4 className="p-2 flex-fill">{ts('cashier_ui.idvalidation.id_expired_matched')}</h4>
      <p className="text-muted">
        <small>{ts('cashier_ui.idvalidation.id_expired_matched.description')}</small>
      </p>
    </div>
  );
};

interface ValidationProps {
  readonly status: CashierCheck;
}

const IDValidationFailed: FC<ValidationProps> = ({ status }) => {
  const { ts } = useTranslation();
  const { scannedInformation, licenseWarnings, user } = status;

  const hasFirstNameMismatch = licenseWarnings.includes('firstNameMismatch');
  const hasLastNameMismatch = licenseWarnings.includes('lastNameMismatch');
  const hasDobMismatch = licenseWarnings.includes('dobMismatch');
  const hasIDExpired = licenseWarnings.includes('expired');

  return (
    <div className="d-flex flex-wrap flex-column align-content-center text-center">
      <div className="p-2 text-center flex-fill text-danger">
        <MaterialIcon large name="dangerous" />
      </div>
      <h4 className="p-2 flex-fill">{ts('cashier_ui.idvalidation.id_mismatch')}</h4>
      <p className="text-muted">
        <small>{ts('cashier_ui.idvalidation.id_mismatch.description')}</small>
      </p>
      <hr />
      <Table>
        <thead>
          <tr>
            <th scope="col" />
            <th scope="col">{ts('common.account')}</th>
            <th scope="col">{ts('id')}</th>
          </tr>
        </thead>
        <tbody>
          {hasFirstNameMismatch && (
            <tr>
              <th scope="row">{ts('common.first_name')}</th>
              <td>{user.firstName}</td>
              <td className="text-danger">
                {scannedInformation?.firstName ? (
                  <strong>{scannedInformation.firstName}</strong>
                ) : (
                  <i>{ts('common.not_provided')}</i>
                )}
              </td>
            </tr>
          )}
          {hasLastNameMismatch && (
            <tr>
              <th scope="row">{ts('common.last_name')}</th>
              <td>{user.lastName}</td>
              <td className="text-danger">
                {scannedInformation?.lastName ? (
                  <strong>{scannedInformation.lastName}</strong>
                ) : (
                  <i>{ts('common.not_provided')}</i>
                )}
              </td>
            </tr>
          )}
          {hasDobMismatch && (
            <tr>
              <th scope="row">{ts('common.date_of_birth')}</th>
              <td>
                {user.bornAt ? (
                  <DateT value={user.bornAt} />
                ) : (
                  <i className="text-danger">{ts('common.not_on_record')}</i>
                )}
              </td>
              <td className="text-danger">
                {scannedInformation?.bornAt ? (
                  <strong>
                    <DateT value={scannedInformation.bornAt} />
                  </strong>
                ) : (
                  <i>{ts('common.not_provided')}</i>
                )}
              </td>
            </tr>
          )}
          {hasIDExpired && (
            <tr>
              <th scope="row">{ts('cashier_ui.idvalidation.id_expiration')}</th>
              <td>
                <i className="text-muted">{ts('na')}</i>
              </td>
              <td className="text-danger">
                <strong>{ts('cashier_ui.idvalidation.id_expired')}</strong>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};
